'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';
import { useAuth } from '@/contexts/AuthContext';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const resourcesRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (resourcesRef.current && !resourcesRef.current.contains(e.target as Node)) {
        setIsResourcesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigationItems = [
    { label: 'Cohort', href: '/cohort', icon: 'AcademicCapIcon', external: false },
    { label: 'Talent Hub', href: 'https://talent.gopmm.in/', icon: 'UserGroupIcon', external: true },
    { label: 'Jobs', href: '/jobs-page', icon: 'BriefcaseIcon' },
    { label: 'Events', href: 'https://luma.com/GoPMM', icon: 'CalendarIcon', external: true },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-base ${
          isScrolled ? 'bg-[#1a1f2e] shadow-md' : 'bg-[#1a1f2e]'
        } ${className}`}
      >
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo Section */}
            <Link href="/homepage" className="flex items-center space-x-3 group">
              <div className="relative w-10 h-10 lg:w-12 lg:h-12 rounded-xl overflow-hidden">
                <Image
                  src="/assets/images/C_-_GoPMM_2_-_Square-1770356625482.png"
                  alt="GoPMM Community Logo"
                  fill
                  className="object-contain transition-transform duration-base group-hover:scale-105"
                  priority
                />
              </div>
              <span className="text-xl lg:text-2xl font-headline font-bold text-white leading-none">
                GoPMM
              </span>
            </Link>

            {/* Desktop Navigation - Centered */}
            <nav className="hidden lg:flex items-center space-x-2 absolute left-1/2 transform -translate-x-1/2">
              {navigationItems.map((item) => (
                <Link
                  key={item.label}
                  href={item.href}
                  {...((item as any).external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  className="flex items-center space-x-2 px-5 py-2.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-base group"
                >
                  <Icon
                    name={item.icon as any}
                    size={20}
                    className="text-gray-400 group-hover:text-white transition-colors duration-base"
                  />
                  <span className="font-body font-medium text-base">{item.label}</span>
                </Link>
              ))}

              {/* Resources Dropdown */}
              <div
                ref={resourcesRef}
                className="relative"
              >
                <button
                  className="flex items-center space-x-2 px-5 py-2.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/5 group"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => setIsResourcesOpen((prev) => !prev)}
                >
                  <Icon
                    name="BookOpenIcon"
                    size={20}
                    className="text-gray-400 group-hover:text-white"
                  />
                  <span className="font-body font-medium text-base">Resources</span>
                  <Icon
                    name="ChevronDownIcon"
                    size={16}
                    className={`text-gray-400 group-hover:text-white ${
                      isResourcesOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {/* Dropdown Menu */}
                {isResourcesOpen && (
                  <div
                    className="absolute top-full left-0 mt-1 w-48 bg-[#1a1f2e] border border-white/10 rounded-xl shadow-xl overflow-hidden z-50"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <Link
                      href="/blogs"
                      onClick={() => setIsResourcesOpen(false)}
                      className="flex items-center space-x-3 px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5"
                    >
                      <Icon name="DocumentTextIcon" size={18} className="text-gray-400" />
                      <span className="font-body font-medium">Blogs</span>
                    </Link>
                  </div>
                )}
              </div>
            </nav>

            {/* CTA Button - Gradient */}
            <div className="hidden lg:flex items-center">
              <Link
                href="/cohort"
                className="px-6 py-2.5 bg-gradient-to-r from-[#ff6b6b] via-[#ee5a6f] to-[#c44569] text-white font-headline font-semibold rounded-lg hover:shadow-lg hover:shadow-pink-500/30 hover:-translate-y-0.5 transition-all duration-base"
              >
                Join Cohort 3
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 rounded-md hover:bg-white/5 transition-colors duration-base"
              aria-label="Toggle mobile menu"
            >
              <Icon
                name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
                size={24}
                className="text-white"
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-[#1a1f2e] lg:hidden"
          style={{ top: '64px' }}
        >
          <nav className="container mx-auto px-4 py-6 space-y-2">
            {navigationItems.map((item) => (
              <Link
                key={item.label}
                href={item.href}
                {...((item as any).external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center space-x-3 px-4 py-3 rounded-md text-gray-300 hover:bg-white/5 hover:text-white transition-all duration-base group"
              >
                <Icon
                  name={item.icon as any}
                  size={24}
                  className="text-gray-400 group-hover:text-white transition-colors duration-base"
                />
                <span className="font-body font-medium text-lg">{item.label}</span>
              </Link>
            ))}

            {/* Resources in mobile */}
            <Link
              href="/blogs"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center space-x-3 px-4 py-3 rounded-md text-gray-300 hover:bg-white/5 hover:text-white transition-all duration-base group"
            >
              <Icon name="DocumentTextIcon" size={24} className="text-gray-400 group-hover:text-white transition-colors duration-base" />
              <span className="font-body font-medium text-lg">Blog</span>
            </Link>

            <div className="pt-4 space-y-3 border-t border-gray-700">
              <Link
                href="/cohort"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full px-4 py-3 text-center bg-gradient-to-r from-[#ff6b6b] via-[#ee5a6f] to-[#c44569] text-white font-headline font-semibold rounded-lg hover:shadow-lg transition-all duration-base"
              >
                Join Cohort 3
              </Link>
            </div>
          </nav>
        </div>
      )}

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-[#1a1f2e] border-t border-gray-700 shadow-lg">
        <div className="container mx-auto px-4 py-3">
          <Link
            href="/cohort"
            className="block w-full px-6 py-3 text-center bg-gradient-to-r from-[#ff6b6b] via-[#ee5a6f] to-[#c44569] text-white font-headline font-semibold rounded-lg hover:shadow-lg transition-all duration-base"
            style={{ minHeight: '48px' }}
          >
            Join Cohort 3
          </Link>
        </div>
      </div>
    </>
  );
};

export default Header;